// firebaseConfig.js
// This is the firebase config for the web apps
import { initializeApp } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import { getAnalytics } from 'firebase/analytics';
import { getRemoteConfig } from 'firebase/remote-config';
import type { Messaging } from 'firebase/messaging';
import { deleteInstallations, getInstallations } from 'firebase/installations';

const firebaseConfig = {
  apiKey: 'AIzaSyBYz_pwtrHojLy1rrCjDqQHtZhsKPJ8EVU',
  authDomain: 'api-project-338116004413.firebaseapp.com',
  databaseURL: 'https://api-project-338116004413.firebaseio.com',
  projectId: 'api-project-338116004413',
  storageBucket: 'api-project-338116004413.firebasestorage.app',
  messagingSenderId: '338116004413',
  appId: '1:338116004413:web:6232eaddc1f11e0b2a7854',
  measurementId: 'G-TEFP3RXGF8',
};
// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Handle Firebase installations "not-registered" error (Fixes BLOOM-FRONTEND-WEB-EG)
// On iOS WKWebView, the OS can clear IndexedDB, causing the local installation ID to become
// stale. Firebase's internal token refresh then fails with an unhandled promise rejection.
// We catch this and delete the corrupted installation so Firebase re-registers on next use.
let installationRecoveryAttempts = 0;
const MAX_INSTALLATION_RECOVERY_ATTEMPTS = 3;
let installationRecoveryInProgress = false;
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', async (event) => {
    const error = event.reason;
    if (
      error?.code === 'installations/not-registered' ||
      (error?.message && error.message.includes('installations/not-registered'))
    ) {
      if (installationRecoveryInProgress) return;
      if (installationRecoveryAttempts >= MAX_INSTALLATION_RECOVERY_ATTEMPTS) {
        console.warn('[FIREBASE] Max installation recovery attempts reached, skipping');
        return;
      }
      installationRecoveryInProgress = true;
      installationRecoveryAttempts++;
      event.preventDefault();
      console.warn(
        `[FIREBASE] Installation not registered, clearing corrupted state (attempt ${installationRecoveryAttempts}/${MAX_INSTALLATION_RECOVERY_ATTEMPTS})...`,
      );
      try {
        const installations = getInstallations(app);
        await deleteInstallations(installations);
        console.warn('[FIREBASE] Corrupted installation deleted, will re-register on next use');
      } catch (deleteError) {
        console.warn('[FIREBASE] Failed to delete installation:', deleteError);
      } finally {
        installationRecoveryInProgress = false;
      }
    }
  });
}

// Lazy-initialized messaging instance (to avoid errors on unsupported browsers)
let messagingInstance: Messaging | null = null;
let messagingChecked = false;

/**
 * Get Firebase Messaging instance with browser support check.
 * Returns null if the browser doesn't support Firebase Messaging.
 * Fixes: BLOOM-FRONTEND-WEB-X4 (messaging/unsupported-browser on Android WebView)
 * Also fixes iOS WKWebView crash (navigator.serviceWorker undefined)
 *
 * Uses dynamic import for firebase/messaging to avoid pulling the module into the
 * main bundle via static imports (which causes unhandled rejections on native platforms).
 */
export const getMessagingInstance = async (): Promise<Messaging | null> => {
  if (messagingChecked) {
    return messagingInstance;
  }

  try {
    // Never initialize web Firebase Messaging on native platforms (iOS/Android).
    // Native platforms use the Capacitor Firebase Messaging plugin instead.
    if (Capacitor.isNativePlatform()) {
      messagingChecked = true;
      console.warn('[FIREBASE] Skipping web messaging on native platform');
      return null;
    }

    const { isSupported, getMessaging } = await import('firebase/messaging');
    const supported = await isSupported();
    messagingChecked = true;

    if (supported) {
      messagingInstance = getMessaging(app);
      return messagingInstance;
    }

    console.warn('[FIREBASE] Messaging not supported in this browser');
    return null;
  } catch (error) {
    messagingChecked = true;
    console.warn('[FIREBASE] Error checking messaging support:', error);
    return null;
  }
};

// Initialize Analytics
export const analytics = getAnalytics(app);

// Initialize Remote Config with defaults
export const remoteConfig = getRemoteConfig(app);
// Use 0 in development for instant updates, 1 hour in production
remoteConfig.settings.minimumFetchIntervalMillis = import.meta.env.DEV ? 0 : 3600000;
remoteConfig.defaultConfig = {
  plaid_portfolio_prd: false,
  plaid_portfolio_dev: false,
  plaid_cache_ttl_minutes: 240,
  plaid_max_daily_refreshes: 5,
  plaid_cache_enabled: true,
};

// Enable debugView mode
// setAnalyticsCollectionEnabled(analytics, true); // <----- only for testing
